/**
 * Trusted Brokers Page
 *
 * Lists verified brokers (Agent.isVerified = true) from the backend in two
 * tabs: Companies (agents affiliated with a company) and Individuals (solo
 * brokers). Replaces the previous mock-data implementation.
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import Navigation from '../../components/feature/Navigation';
import Footer from '../../components/feature/Footer';
import type { BadgeTier } from '../../components/verification';
import BrokerCard, { type TrustedBroker, type BrokerType } from './BrokerCard';
import Pagination from '../../components/base/Pagination';
import SegmentedControl from '../../components/base/SegmentedControl';
import SelectMenu from '../../components/base/SelectMenu';
import { apiRequest } from '../../utils/apiClient';
import { formatAmount } from '../../utils/numberFormat';
import { useAuth } from '../../hooks/useAuth';
import { BrandRule } from '../../components/brand/BrandRule';

const PAGE_SIZE = 20;

// TrustedBroker / BrokerType are imported from ./BrokerCard (shared card shape).

// Raw shape returned by GET /api/agents
interface ApiAgent {
  id: string;
  displayName?: string | null;
  bio?: string | null;
  avatarUrl?: string | null;
  businessPhone?: string | null;
  businessEmail?: string | null;
  whatsapp?: string | null;
  companyId?: string | null;
  isVerified: boolean;
  falVerificationStatus?: string | null;
  currentBadgeType?: string | null;
  avgRating?: number | null;
  totalReviews?: number | null;
  totalSold?: number | null;
  totalRented?: number | null;
  totalListings?: number | null;
  region?: string | null;
  regionAr?: string | null;
  districtName?: string | null;
  districtNameAr?: string | null;
  createdAt?: string | null;
  user?: {
    firstName?: string | null;
    lastName?: string | null;
    avatarUrl?: string | null;
  } | null;
  company?: {
    id?: string;
    name?: string | null;
    nameAr?: string | null;
    slug?: string | null;
    logoUrl?: string | null;
  } | null;
}

interface AgentsResponse {
  data?: ApiAgent[];
  meta?: { total?: number; page?: number; pageSize?: number; totalPages?: number };
}

// Raw shape returned by GET /api/developers
interface ApiDeveloper {
  id: string;
  name?: string | null;
  nameAr?: string | null;
  slug: string;
  logoUrl?: string | null;
  headquartersCity?: string | null;
  region?: string | null;
  foundedYear?: number | null;
  isVerified?: boolean | null;
  createdAt?: string | null;
  projectsCount?: number | null;
  listingsCount?: number | null;
}

interface DevelopersResponse {
  data?: ApiDeveloper[];
  meta?: { total?: number; page?: number; pageSize?: number; totalPages?: number };
}

const cities = [
  { value: '', label: 'All Cities', labelAr: 'جميع المدن' },
  { value: 'riyadh', label: 'Riyadh', labelAr: 'الرياض' },
  { value: 'jeddah', label: 'Jeddah', labelAr: 'جدة' },
  { value: 'dammam', label: 'Dammam', labelAr: 'الدمام' },
];

const sortOptions = [
  { value: 'rating' as const, label: 'Top Rated', labelAr: 'الأعلى تقييماً' },
  { value: 'transactions' as const, label: 'Most Deals', labelAr: 'الأكثر صفقات' },
  { value: 'experience' as const, label: 'Most Experienced', labelAr: 'الأكثر خبرة' },
];

type SortKey = 'rating' | 'transactions' | 'experience';

// The search page's pill recipe (THEME.md decision D3 for the chosen one).
const CITY_PILL = 'inline-flex h-10 shrink-0 items-center rounded-full border px-4 text-[0.875rem] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pv-color-primary)]/30';
const CITY_PILL_ON = 'border-[var(--pv-color-primary)] bg-[var(--pv-color-surface-sunken)] text-[var(--pv-color-primary)]';
const CITY_PILL_OFF = 'border-[var(--pv-border-strong)] bg-[var(--pv-color-surface)] text-[var(--pv-color-text)] hover:border-[var(--pv-color-primary)]';
const SOFT_BUTTON = 'inline-flex h-10 items-center rounded-full border border-[var(--pv-border-strong)] px-5 text-[0.875rem] font-medium text-[var(--pv-color-text)] transition-colors hover:border-[var(--pv-color-primary)]';

function badgeTierFromAgent(agent: ApiAgent): BadgeTier {
  // Map backend badge / FAL status to UI badge tier. Conservative defaults
  // until the Phase-5 badge system is fully wired.
  const t = (agent.currentBadgeType || '').toUpperCase();
  if (t.includes('GOLD')) return 'gold';
  if (t.includes('COMPANY')) return 'company';
  return 'verified';
}

function yearsFrom(createdAt?: string | null): number {
  if (!createdAt) return 0;
  const created = new Date(createdAt);
  if (Number.isNaN(created.getTime())) return 0;
  const ms = Date.now() - created.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24 * 365)));
}

function mapAgentToBroker(agent: ApiAgent): TrustedBroker {
  const userFullName = [agent.user?.firstName, agent.user?.lastName].filter(Boolean).join(' ').trim();
  const name = agent.displayName || userFullName || 'Broker';
  const isCompanyAffiliated = Boolean(agent.companyId);

  return {
    id: agent.id,
    brokerType: isCompanyAffiliated ? 'company' : 'individual',
    name,
    nameAr: name, // backend doesn't currently expose a separate Arabic display name on the agent
    photo: agent.avatarUrl || agent.user?.avatarUrl || undefined,
    companyName: agent.company?.name || undefined,
    companyNameAr: agent.company?.nameAr || agent.company?.name || undefined,
    companyLogo: agent.company?.logoUrl || undefined,
    companySlug: agent.company?.slug || undefined,
    badgeTier: badgeTierFromAgent(agent),
    rating: typeof agent.avgRating === 'number' ? agent.avgRating : 0,
    reviewCount: agent.totalReviews ?? 0,
    transactionCount: (agent.totalSold ?? 0) + (agent.totalRented ?? 0),
    activeListings: agent.totalListings ?? 0,
    yearsExperience: yearsFrom(agent.createdAt),
    specialties: [],
    city: agent.region || '',
    cityAr: agent.regionAr || agent.region || '',
    districts: agent.districtName ? [agent.districtName] : [],
    isOnline: false,
    responseRate: 0,
    phone: agent.businessPhone || agent.whatsapp || undefined,
    whatsapp: agent.whatsapp || agent.businessPhone || undefined,
    email: agent.businessEmail || undefined,
  };
}

function mapDeveloperToBroker(d: ApiDeveloper): TrustedBroker {
  const name = d.name || 'Developer';
  const nameAr = d.nameAr || name;
  const location = d.headquartersCity || d.region || '';
  return {
    id: d.id,
    brokerType: 'developer',
    name,
    nameAr,
    // Organisations render from the company* fields in BrokerCard.
    companyName: name,
    companyNameAr: nameAr,
    companyLogo: d.logoUrl || undefined,
    companySlug: d.slug,
    establishedYear: d.foundedYear || undefined,
    badgeTier: 'company',
    rating: 0,
    reviewCount: 0,
    transactionCount: d.projectsCount ?? 0, // shown as "Projects" for developers
    activeListings: d.listingsCount ?? 0,
    yearsExperience: d.foundedYear ? Math.max(0, new Date().getFullYear() - d.foundedYear) : 0,
    specialties: [],
    city: location,
    cityAr: location,
    districts: [],
    isOnline: false,
    responseRate: 0,
  };
}

export default function TrustedBrokersPage() {
  const { i18n } = useTranslation(['common', 'verification']);
  const isRTL = i18n.language === 'ar';
  const { user } = useAuth();

  // Deep-link the tab via ?type= (the "Search for…" nav menu uses this).
  const [searchParams] = useSearchParams();
  const typeParam = searchParams.get('type');
  const initialTab: BrokerType =
    typeParam === 'individual' || typeParam === 'developer' || typeParam === 'company'
      ? typeParam
      : 'individual';
  const [activeTab, setActiveTab] = useState<BrokerType>(initialTab);
  const [brokers, setBrokers] = useState<TrustedBroker[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [cityFilter, setCityFilter] = useState('');
  const [sortBy, setSortBy] = useState<SortKey>('rating');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalResults, setTotalResults] = useState(0);

  // Reset to page 1 whenever the tab, city, or sort changes — the previous
  // page number no longer makes sense against a new result set.
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, cityFilter, sortBy]);

  // Map our sort keys to backend-supported sort values. The backend supports
  // sorting by avgRating ('rating'), totalReviews ('reviews'), totalListings
  // ('listings'), and createdAt ('newest'). "Most deals" and "most experienced"
  // don't have direct backend equivalents — fall back to rating so results
  // remain stable rather than silently re-sorting on the client.
  const sortMap: Record<SortKey, string> = {
    rating: 'rating',
    transactions: 'listings',
    experience: 'newest',
  };

  // Fetch a single tab's page from the backend whenever any of the inputs change.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    // Developers are a separate entity served by /api/developers; individual and
    // company brokers come from /api/agents.
    if (activeTab === 'developer') {
      const devParams = new URLSearchParams({
        isVerified: 'true',
        page: String(currentPage),
        pageSize: String(PAGE_SIZE),
        sort: sortBy === 'transactions' ? 'projects' : sortBy === 'experience' ? 'newest' : 'name',
      });
      if (cityFilter) devParams.set('city', cityFilter);

      apiRequest<DevelopersResponse>(`/developers?${devParams.toString()}`)
        .then((res) => {
          if (cancelled) return;
          setBrokers((res.data ?? []).map(mapDeveloperToBroker));
          setTotalResults(res.meta?.total ?? 0);
          setTotalPages(Math.max(1, res.meta?.totalPages ?? 1));
        })
        .catch((err) => {
          if (cancelled) return;
          setError(err instanceof Error ? err.message : 'Failed to load developers');
          setBrokers([]);
          setTotalResults(0);
          setTotalPages(1);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
      return () => { cancelled = true; };
    }

    const params = new URLSearchParams({
      isVerified: 'true',
      type: activeTab,
      page: String(currentPage),
      pageSize: String(PAGE_SIZE),
      sort: sortMap[sortBy],
    });
    if (cityFilter) params.set('city', cityFilter);

    apiRequest<AgentsResponse>(`/agents?${params.toString()}`)
      .then((res) => {
        if (cancelled) return;
        setBrokers((res.data ?? []).map(mapAgentToBroker));
        setTotalResults(res.meta?.total ?? 0);
        setTotalPages(Math.max(1, res.meta?.totalPages ?? 1));
      })
      .catch((err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Failed to load brokers';
        setError(message);
        setBrokers([]);
        setTotalResults(0);
        setTotalPages(1);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // sortMap is a static constant in this scope; including it as a dep is unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, cityFilter, sortBy]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    // Scroll to the top of the broker grid so the user sees the new page.
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[var(--pv-color-surface)] text-[var(--pv-color-text-muted)]" dir={isRTL ? 'rtl' : 'ltr'}>
      <Navigation />

      {/* Header: the title, the three directories, and the filters, in the same
          calm pattern as /search (owner, 2026-10-07). */}
      <header className="border-b border-[var(--pv-border-subtle)]">
        <div className="mx-auto max-w-6xl px-4 pb-5 pt-8 sm:px-6">
          <h1 className="text-[1.625rem] font-bold text-[var(--pv-color-primary)] sm:text-[2rem]">
            {isRTL ? 'مسوّق ووسيط عقاري' : 'Trusted Brokers'}
          </h1>
          <BrandRule className="mt-3" />
          <p className="mt-1.5 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {isRTL
              ? 'تواصل مع وسطاء وشركات عقارية تم التحقق منها'
              : 'Connect with verified real estate professionals'}
          </p>

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3">
            <SegmentedControl<BrokerType>
              value={activeTab}
              onChange={(tab) => { setActiveTab(tab); setCityFilter(''); }}
              options={[
                { value: 'individual', label: isRTL ? 'أفراد' : 'Individuals', icon: 'ri-user-line' },
                { value: 'company', label: isRTL ? 'الشركات' : 'Companies', icon: 'ri-building-2-line' },
                { value: 'developer', label: isRTL ? 'المطورون العقاريون' : 'Developers', icon: 'ri-building-4-line' },
              ]}
              ariaLabel={isRTL ? 'نوع المختص' : 'Directory'}
              size="sm"
            />
            {/* Brokers Map entry — flag-gated until launch. */}
            {import.meta.env.VITE_BROKER_MAP_ENABLED === 'true' && (
              <Link to="/brokers/map" className={`${SOFT_BUTTON} gap-1.5`}>
                <i className="ri-road-map-line text-base leading-none" aria-hidden="true"></i>
                {isRTL ? 'عرض على الخريطة' : 'View on map'}
              </Link>
            )}
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2 overflow-x-auto no-scrollbar" role="group" aria-label={isRTL ? 'المدينة' : 'City'}>
              {cities.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  aria-pressed={cityFilter === c.value}
                  onClick={() => setCityFilter(c.value)}
                  className={`${CITY_PILL} ${cityFilter === c.value ? CITY_PILL_ON : CITY_PILL_OFF}`}
                >
                  {isRTL ? c.labelAr : c.label}
                </button>
              ))}
            </div>
            <div className="w-56">
              <SelectMenu<SortKey>
                id="brokers-sort"
                label={isRTL ? 'ترتيب حسب' : 'Sort by'}
                hideLabel
                size="sm"
                value={sortBy}
                onChange={setSortBy}
                options={sortOptions.map((opt) => ({ value: opt.value, label: isRTL ? opt.labelAr : opt.label }))}
                leadingIcon={<i className="ri-sort-desc" />}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="mx-auto max-w-6xl px-4 pb-16 pt-6 sm:px-6">
        {loading ? (
          // The same grid as the result, so nothing jumps when it lands.
          <ul role="list" aria-hidden="true" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 6 }, (_, i) => (
              <li key={i} className="h-[11.5rem] animate-pulse rounded-[16px] bg-[var(--pv-color-surface-sunken)] motion-reduce:animate-none" />
            ))}
          </ul>
        ) : error ? (
          <div className="mx-auto max-w-md py-16 text-center">
            <i className="ri-wifi-off-line text-[2.25rem] leading-none text-[var(--pv-color-text-muted)]" aria-hidden="true"></i>
            <h2 className="mt-4 text-[1.25rem] font-semibold text-[var(--pv-color-text)]">
              {isRTL ? 'تعذّر تحميل القائمة' : 'Could not load brokers'}
            </h2>
            <button type="button" onClick={() => window.location.reload()} className={`${SOFT_BUTTON} mt-6`}>
              {isRTL ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        ) : brokers.length === 0 ? (
          <div className="mx-auto max-w-md py-16 text-center">
            <i
              className={`${activeTab === 'company' ? 'ri-building-2-line' : activeTab === 'developer' ? 'ri-building-4-line' : 'ri-user-search-line'} text-[2.25rem] leading-none text-[var(--pv-color-text-muted)]`}
              aria-hidden="true"
            ></i>
            <h2 className="mt-4 text-[1.25rem] font-semibold text-[var(--pv-color-text)]">
              {isRTL
                ? (activeTab === 'company' ? 'لا توجد شركات موثّقة بعد' : activeTab === 'developer' ? 'لا يوجد مطورون موثّقون بعد' : 'لا يوجد وسطاء موثّقون بعد')
                : (activeTab === 'company' ? 'No verified companies yet' : activeTab === 'developer' ? 'No verified developers yet' : 'No verified brokers yet')
              }
            </h2>
            <p className="mt-2 text-[0.9375rem] text-[var(--pv-color-text-muted)]">
              {cityFilter
                ? (isRTL ? 'جرب مسح فلتر المدينة' : 'Try clearing the city filter')
                : (isRTL
                  ? 'سيظهر الوسطاء هنا بعد اعتماد ترخيص الفال من قبل الإدارة'
                  : 'Brokers appear here after FAL license approval by an admin')
              }
            </p>
            {cityFilter && (
              <button type="button" onClick={() => { setCityFilter(''); setSortBy('rating'); }} className={`${SOFT_BUTTON} mt-6`}>
                {isRTL ? 'مسح الفلاتر' : 'Clear Filters'}
              </button>
            )}
          </div>
        ) : (
          <>
            {/* The count, once (the pages are in the pagination below). */}
            <p className="mb-4 text-[0.9375rem] text-[var(--pv-color-text-muted)] tabular-nums">
              {isRTL
                ? `${formatAmount(totalResults)} نتيجة`
                : `${formatAmount(totalResults)} result${totalResults === 1 ? '' : 's'}`}
            </p>

            <ul role="list" className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {brokers.map((broker) => (
                <li key={broker.id}>
                  <BrokerCard broker={broker} isRTL={isRTL} />
                </li>
              ))}
            </ul>

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              className="mt-10"
            />
          </>
        )}

        {/* CTA — only meaningful for signed-out visitors. Signed-in brokers and
            company members already have an account; sending them through the
            registration flow again is confusing. */}
        {!user && (
          <div className="mt-16 border-t border-[var(--pv-border-subtle)] pt-12 text-center">
            <div>
              <h2 className="text-[1.25rem] font-semibold text-[var(--pv-color-primary)]">
                {activeTab === 'company'
                  ? (isRTL ? 'هل لديك شركة عقارية؟' : 'Own a real estate company?')
                  : activeTab === 'developer'
                    ? (isRTL ? 'هل أنت مطور عقاري؟' : 'Are you a real estate developer?')
                    : (isRTL ? 'هل أنت وسيط عقاري؟' : 'Are you a broker?')
                }
              </h2>
              <p className="mx-auto mb-6 mt-2 max-w-sm text-[0.9375rem] text-[var(--pv-color-text-muted)]">
                {activeTab === 'developer'
                  ? (isRTL
                    ? 'اعرض مشاريعك على منصة المستثمرين وتواصل مع المستثمرين'
                    : 'Showcase your projects on the Investors Hub and reach investors')
                  : (isRTL
                    ? 'انضم إلى قائمة الوسطاء الموثوقين واحصل على شارة التحقق'
                    : 'Join our verified network and grow your business')}
              </p>
              <Link
                to={activeTab === 'company' ? '/register?type=company' : activeTab === 'developer' ? '/register?type=developer' : '/register?type=broker'}
                className="inline-flex h-11 items-center gap-2 rounded-full bg-[var(--pv-color-primary)] px-6 text-[0.9375rem] font-semibold text-white transition-opacity hover:opacity-90 dark:text-[var(--pv-color-surface)]"
              >
                <i className="ri-shield-check-line text-base leading-none" aria-hidden="true"></i>
                {isRTL ? 'سجل الآن' : 'Get Verified'}
              </Link>
            </div>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
